import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import Input from "../../components/Atomos/Inputs/Input";
import CalendarInput from "../../components/Atomos/Inputs/CalendarInput";
import InvoicePdfPreview from "../../components/Atomos/PdfPreview/InvoicePdfPreview";
import { upsertPayment, type BillingHistoryDoc } from "../../Services/Billing";
import {
  BALANCE_ADJUSTMENT_KIND,
  recordOfficeBalanceMovement,
  resolvePerformedBy,
} from "../../utils/accountingOfficeBalance";
import { useAuth } from "../../store/AuthContext";
import type { AccountingOfficeDetail } from "../../utils/accountingOfficeDetail";
import { officeDisplayName } from "../../utils/accountingOfficeDetail";
import { isCancelledBillingDoc } from "../../utils/billingStatus";
import {
  buildPaymentReceiptInputFromDoc,
  generateAccountingPaymentReceiptPDFBlob,
} from "../../utils/generateAccountingInvoicePDF";
import {
  AccountingModalError,
  AccountingModalFirmSubtitle,
  AccountingModalFooter,
  AccountingModalSectionTitle,
  displayToIso,
  formatMoney,
  isoToDisplay,
} from "./accountingModalUi";
import { DIRECT_PAYMENT_CODE } from "../../utils/billingConstants";
import { FEE_TYPE_LABEL_CLASS, FIELD_VALUE_CLASS } from "./accountingFirmPanels";
import { useInvoiceAuditLogger } from "../../utils/invoiceAudit";
import {
  buildPaymentChanges,
  buildPaymentConcepts,
  summarizeChanges,
  summarizeConcepts,
} from "../../utils/billingAuditDiff";

const RECEIPT_PREVIEW_DEBOUNCE_MS = 400;

type EditPaymentModalProps = {
  office: AccountingOfficeDetail;
  payment: BillingHistoryDoc | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

function num(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export default function EditPaymentModal({
  office,
  payment,
  isOpen,
  onClose,
  onSaved,
}: EditPaymentModalProps): React.ReactElement {
  const officeName = officeDisplayName(office);
  const isSent = payment?.isSent === true;
  const isCancelled = payment ? isCancelledBillingDoc(payment) : false;
  const isReadOnly = isSent || isCancelled;
  const { logEvent } = useInvoiceAuditLogger();
  const { loggedPerson, tokenDecoded } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [description, setDescription] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [amount, setAmount] = useState("0");
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const allocations = useMemo(
    () => (Array.isArray(payment?.allocations) ? payment!.allocations : []),
    [payment],
  );
  const isDirectPayment =
    payment?.code === DIRECT_PAYMENT_CODE || allocations.length === 0;

  useEffect(() => {
    if (!isOpen || !payment) {
      return;
    }
    setSaving(false);
    setError("");
    setDescription(payment.description ?? "");
    setPaymentDate(payment.paymentDate ?? "");
    setAmount(String(Number(payment.amount) || 0));
    setPdfError("");
  }, [isOpen, payment]);

  const workingPayment = useMemo<BillingHistoryDoc | null>(() => {
    if (!payment) {
      return null;
    }
    return {
      ...payment,
      description: description.trim() || undefined,
      paymentDate,
      amount: isDirectPayment ? num(amount) : Number(payment.amount) || 0,
    };
  }, [payment, description, paymentDate, amount, isDirectPayment]);

  const receiptSignature = useMemo(
    () => (workingPayment ? JSON.stringify(workingPayment) : ""),
    [workingPayment],
  );

  useEffect(() => {
    if (!isOpen || !workingPayment || Number(workingPayment.amount) <= 0) {
      setPdfBlob(null);
      setPdfGenerating(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        setPdfGenerating(true);
        setPdfError("");
        try {
          const blob = await generateAccountingPaymentReceiptPDFBlob(
            buildPaymentReceiptInputFromDoc(workingPayment, office),
          );
          if (cancelled) {
            return;
          }
          setPdfBlob(blob);
        } catch {
          if (!cancelled) {
            setPdfError("Unable to load payment receipt preview.");
            setPdfBlob(null);
          }
        } finally {
          if (!cancelled) {
            setPdfGenerating(false);
          }
        }
      })();
    }, RECEIPT_PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, receiptSignature, office]);

  const handleSave = async () => {
    if (!payment) {
      return;
    }

    if (isDirectPayment && num(amount) <= 0) {
      setError("Enter a payment amount greater than zero.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const updated: BillingHistoryDoc = {
        ...payment,
        description: description.trim() || undefined,
        paymentDate,
        ...(isDirectPayment ? { amount: num(amount) } : {}),
      };
      await upsertPayment(updated);
      const changes = buildPaymentChanges(payment, updated);
      const concepts = buildPaymentConcepts(updated);
      const conceptSummary = summarizeConcepts(concepts);
      void logEvent({
        billingDocId: updated.id,
        billingDocType: "payment",
        partyKey: updated.partyKey,
        partyId: office.id,
        officeId: office.id,
        firmId: String(office.firm ?? "") || null,
        invoiceNumber: updated.reference,
        action: "edited",
        changes,
        concepts,
        detail: conceptSummary
          ? `${summarizeChanges(changes)} · concepts: ${conceptSummary}`
          : summarizeChanges(changes),
      });

      if (isDirectPayment) {
        const delta = num(amount) - (Number(payment.amount) || 0);
        if (delta !== 0) {
          const { performedById, performedByName } = resolvePerformedBy(
            loggedPerson,
            tokenDecoded,
          );
          await recordOfficeBalanceMovement({
            officeId: office.id,
            firmId: String(office.firm ?? "") || null,
            balanceDelta: -delta,
            ledgerAmount: delta,
            kind: BALANCE_ADJUSTMENT_KIND.directPaymentEditAdjustment,
            reasonCode: "system_payment",
            sourceLabel: `Direct payment amount updated on ${payment.reference || payment.id}`,
            paymentId: payment.id,
            performedById,
            performedByName,
          });
        }
      }

      onSaved();
      onClose();
    } catch {
      setError("Unable to save payment changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={onClose}
      title="Preview & Edit Payment"
      compact
      compactBodyScrolls
      className="max-w-6xl"
    >
      <div className="flex flex-col gap-5">
        <AccountingModalFirmSubtitle>
          {officeName}
          {payment?.reference ? ` · ${payment.reference}` : ""}
        </AccountingModalFirmSubtitle>

        {isCancelled && (
          <div className="rounded border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-font">
            This payment has been cancelled and cannot be edited.
          </div>
        )}

        {isSent && !isCancelled && (
          <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            This payment receipt has been sent and cannot be edited.
          </div>
        )}

        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div className="min-w-0">
            <div className={FEE_TYPE_LABEL_CLASS}>Reference Number</div>
            <div className={FIELD_VALUE_CLASS}>{payment?.reference ?? "—"}</div>
          </div>
          <div className="min-w-0">
            <div className={FEE_TYPE_LABEL_CLASS}>Payment Method</div>
            <div className="mt-1 text-base font-semibold text-gray-font md:text-lg">
              {payment?.method ?? "—"}
            </div>
          </div>
          <CalendarInput
            title="Payment Date"
            value={isoToDisplay(paymentDate)}
            defaultValue={isoToDisplay(paymentDate)}
            onChange={(date: string) => setPaymentDate(displayToIso(date))}
            type="single"
            error={false}
            disabled={isReadOnly}
          />
          {isDirectPayment ? (
            <Input
              title="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={false}
              disabled={isReadOnly}
            />
          ) : (
            <div className="min-w-0">
              <div className={FEE_TYPE_LABEL_CLASS}>Amount</div>
              <div className={FIELD_VALUE_CLASS}>
                {formatMoney(Number(payment?.amount) || 0)}
              </div>
            </div>
          )}
          <Input
            title="Comments"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={false}
            disabled={isReadOnly}
          />
        </div>

        {allocations.length > 0 && (
          <div>
            <AccountingModalSectionTitle>
              Allocations
            </AccountingModalSectionTitle>
            <div className="mt-3 table-responsive tabledata w-full overflow-x-auto">
              <table className="table min-w-full w-max">
                <thead>
                  <tr className="bg-accent-Default-dark text-white">
                    <th className="px-4 py-2 text-left text-xs font-medium">
                      Category
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((alloc, index) => (
                    <tr
                      key={`${alloc.category ?? "allocation"}-${index}`}
                      className="even:bg-gray-100 text-gray-font text-xs"
                    >
                      <td className="px-4 py-2">{alloc.category ?? "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatMoney(Number(alloc.amount) || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div>
          <AccountingModalSectionTitle>
            Payment Receipt Preview
          </AccountingModalSectionTitle>
          <div className="mt-3 overflow-hidden rounded border border-gray-200 bg-gray-50">
            {pdfError ? (
              <div className="flex min-h-[28rem] items-center justify-center px-4 text-center text-sm text-red-600">
                {pdfError}
              </div>
            ) : (
              <InvoicePdfPreview
                pdfBlob={pdfBlob}
                loading={pdfGenerating}
                loadingMessage="Generating payment receipt preview..."
                emptyMessage="No preview available."
              />
            )}
          </div>
        </div>

        <AccountingModalError message={error} />

        {isReadOnly ? (
          <div className="flex justify-end border-t border-gray-200 pt-4">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={onClose}
            >
              CLOSE
            </button>
          </div>
        ) : (
          <AccountingModalFooter
            onCancel={onClose}
            onSubmit={handleSave}
            submitText="Save Changes"
            saving={saving}
          />
        )}
      </div>
    </Modal>
  );
}
